import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// local imports - types
import calendarLoading from 'assets/icons/loading-calendar.png'
import DateUtil from 'utils/DateUtil'
import { IFilteredDoctorList } from 'types/HealthType'
import { useGetDoctorAppointmentTable } from '../hooks'
import Loader from 'components/ui/Loader'
import AppointmentModalHeader from './AppointmentModalHeader'
import Scheduler from './Scheduler'
import WeekSlider from './WeekSlider'
import ScheduleSidebar from './ScheduleSidebar'

/* Types */
interface IAppointmentModalProps {
  filterDate: Date
  doctors: IFilteredDoctorList[] | []
  selectedDoc: IFilteredDoctorList
  setSelectedDoc: Dispatch<SetStateAction<IFilteredDoctorList | undefined>>
  open: boolean
  closeModal: (reset?: boolean) => void
  openModal: () => void
  showNotification: () => void
}

interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()

  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.appointment')} onClick={onSave} />
    </ActionArea>
  )
}

export default function AppointmentModalWrapper({
  filterDate: dateFromList,
  doctors,
  selectedDoc,
  setSelectedDoc,
  open,
  closeModal,
  openModal,
  showNotification,
}: IAppointmentModalProps) {
  const { t } = useTranslation()

  /* Doctor List reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  useEffect(() => setFilterDate(dateFromList), [dateFromList])
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)

  /* GET Selected Doctor's Appointment Table List | Custom Hook  */
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedAppointment,
    setSelectedAppointment,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyAppointments,
  } = useGetDoctorAppointmentTable(selectedDoc.healthServiceDoctorId, dateFromList)

  /* Open Appointment Sidebar */
  const openAppointmentSidebar = () => {
    setSidebarVisible(true)
    closeModal()
  }

  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    setSidebarVisible(false)
    setSelectedAppointment(undefined)
    showNotification()
    setFilterDate(dateFromList)
    setTableWeek(lastFilteredDate)
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => {
    closeModal(true)
    setFilterDate(dateFromList)
    setSelectedAppointment(undefined)
    setTableWeek(lastFilteredDate)
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        closeOnEscape={false}
        focusOnShow={false}
        closable={true}
        header={<span>{t('appointment-modal.doctor-appointment')}</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '95vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openAppointmentSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!selectedAppointment}
          />
        }>
        {/* Modal Header */}
        <AppointmentModalHeader
          filterDate={filterDate}
          availableDateRange={availableDates}
          setFilterDate={setFilterDate}
          setSelectedDoc={setSelectedDoc}
          doctor={selectedDoc}
          doctors={doctors}
          setTableWeek={setTableWeek}
        />

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <Scheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyAppointments={dailyAppointments}
            selectedAppointment={selectedAppointment}
            setSelectedAppointment={setSelectedAppointment}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedAppointment && (
        <ScheduleSidebar
          openModal={openModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          docInfo={selectedDoc}
          appointmentInfo={selectedAppointment}
        />
      )}
    </>
  )
}

const ActionArea = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: -4px;
  margin-top: 20px;
  border-bottom-right-radius: 15px !important;
  border-bottom-left-radius: 15px !important;
`
const CancelButton = styled(Button)`
  width: 255px !important;
  height: 50px;
  justify-content: center;
  background: #45bfe5;
  border-radius: 10px;

  :disabled {
    opacity: 0.4;
  }
`
const ConfirmButton = styled(Button)`
  width: 255px !important;
  height: 50px;
  justify-content: center;
  background: #45bfe5;
  border-radius: 10px;
`
